import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import type { DirectoryEntry } from '@system-atlas/shared';
import { ApiError, api, type UserRow } from '../lib/api';
import { Alert, Button, Label, Modal, TextInput } from './ui';

interface DeleteUserDialogProps {
  /** Zu loeschender Benutzer; `null` haelt den Dialog geschlossen. */
  user: UserRow | null;
  onClose: () => void;
  /** Nach erfolgreichem Loeschen (Aufrufer laedt Liste neu + zeigt Erfolgsmeldung). */
  onDeleted: (deleted: UserRow) => void;
}

export function DeleteUserDialog({ user, onClose, onDeleted }: DeleteUserDialogProps) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryEntry[]>([]);
  const [selected, setSelected] = useState<DirectoryEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setError(null);
    }
  }, [user?.id]);

  // Typeahead ueber das Benutzerverzeichnis (nur Benutzer als Ziel-Eigentuemer).
  useEffect(() => {
    if (!user || selected) return;
    const term = query.trim();
    const handle = setTimeout(async () => {
      try {
        const { entries } = await api.searchDirectory(term, 'user');
        setResults(entries.filter((e) => e.id !== user.id));
      } catch {
        setResults([]);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [query, selected, user?.id]);

  if (!user) return null;

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteUser(user.id, selected.id);
      onDeleted(user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={t('users.deleteTitle', { name: user.displayName })} onClose={onClose}>
      <form onSubmit={confirm} className="space-y-4">
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
          {t('users.deleteWarning', { name: user.displayName })}
        </div>
        {error && <Alert kind="error">{error}</Alert>}

        <div>
          <Label htmlFor="del-transfer">{t('users.deleteTransferLabel')}</Label>
          {selected ? (
            <div className="flex items-center justify-between rounded border border-slate-200 px-3 py-2 text-sm dark:border-slate-700">
              <span>
                {selected.label} <span className="text-slate-400">({selected.sublabel})</span>
              </span>
              <button
                type="button"
                className="text-slate-400 hover:text-red-600"
                onClick={() => setSelected(null)}
                aria-label={t('common.cancel')}
              >
                ×
              </button>
            </div>
          ) : (
            <TextInput
              id="del-transfer"
              value={query}
              placeholder={t('users.deleteTransferPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
            />
          )}
        </div>

        {!selected && results.length > 0 && (
          <ul className="max-h-44 overflow-auto rounded border border-slate-200 text-sm dark:border-slate-700">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
                  onClick={() => {
                    setSelected(r);
                    setResults([]);
                  }}
                >
                  <span>
                    {r.label} <span className="text-slate-400">({r.sublabel})</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="danger" disabled={busy || !selected}>
            {busy ? t('common.pleaseWait') : t('users.deleteConfirm')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
